import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ChatWidget from "./components/ChatWidget";
import AppLayout from "./pages/AppLayout";
import { AuthProvider, useAuth } from "./components/AuthContext";
import Login from "./pages/Login";
import CarrierAssignedLoads from "./pages/CarrierAssignedLoads";
import CarrierLoadDetail from "./pages/CarrierLoadDetail";
import CarrierLoadBoard from "./pages/CarrierLoadBoard";
import BrokerDashboard from "./pages/BrokerDashboard";
import BrokerCreateLoad from "./pages/BrokerCreateLoad";
import BrokerLoads from "./pages/BrokerLoads";
import BrokerLoadDetail from "./pages/BrokerLoadDetail";
import BrokerCustomers from "./pages/BrokerCustomers";
import BrokerPendingDocuments from "./pages/BrokerPendingDocuments";
import ShipperDashboard from "./pages/ShipperDashboard";
import ShipperLoads from "./pages/ShipperLoads";
import ShipperTracking from "./pages/ShipperTracking";
import ShipperRequestShipment from "./pages/ShipperRequestShipment";
import ShipperProfile from "./pages/ShipperProfile";
import ShipperLoadDetail from "./pages/ShipperLoadDetail";
import BrokerCreateCarrier from "./pages/BrokerCreateCarrier";
import CarrierFactoringHistory from "./pages/CarrierFactoringHistory";
import CarrierProfile from "./pages/CarrierProfile";
import CarrierLoadAlerts from './pages/CarrierLoadAlerts';
import CarrierTracking from './pages/CarrierTracking';
import BrokerCreditCheck from "./pages/BrokerCreditCheck";
import OilfieldLoadBoard from "./pages/OilfieldLoadBoard";
import CarrierCertifications from "./pages/CarrierCertifications";
import WellsiteManager from "./pages/WellsiteManager";
import BrokerFactoringDashboard from "./pages/BrokerFactoringDashboard";
import AccountingDashboard from "./pages/AccountingDashboard";
import AccountingInvoicing from "./pages/AccountingInvoicing";
import AccountingSettlements from "./pages/AccountingSettlements";
import AccountingExports from "./pages/AccountingExports";
import CRMDashboard from "./pages/CRMDashboard";
import CRMContacts from "./pages/CRMContacts";
import TariffCalculator from "./pages/TariffCalculator";
import SAPSettings from "./pages/SAPSettings";
import SustainabilityDashboard from "./pages/SustainabilityDashboard";
import AdminRoles from "./pages/AdminRoles";
import AdminUsers from "./pages/AdminUsers";
import AdminOrganization from "./pages/AdminOrganization";
import SuperAdminOrganizations from "./pages/SuperAdminOrganizations";
import AuditLog from "./pages/AuditLog";
import SecuritySettings from "./pages/SecuritySettings";
import AICopilot from "./pages/AICopilot";
import AlertsDashboard from "./pages/AlertsDashboard";
import FreightAgent from "./pages/FreightAgent";
import BlockchainLedger from "./pages/BlockchainLedger";
import AnalyticsDashboard from "./pages/AnalyticsDashboard";
import CRMChatSessions from "./pages/CRMChatSessions";
import AccountingIntegrations from "./pages/AccountingIntegrations";
import LandingPage from "./pages/LandingPage";
import RateInsights from './pages/RateInsights';
import BrokerTracking from './pages/BrokerTracking';
import BrokerCarrierManagement from './pages/BrokerCarrierManagement';
import QRHandoffs from "./pages/QRHandoffs";
import DocumentSigning from "./pages/DocumentSigning";
import LoadWorkflow from "./pages/LoadWorkflow";
import CarrierPODCapture from "./pages/CarrierPODCapture";
import CarrierLayout from "./pages/CarrierLayout";
import CarrierBackhaulAlerts from "./pages/CarrierBackhaulAlerts.js";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import SmsConsent from "./pages/SmsConsent";

// Protected route wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated } = useAuth();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Landing Page */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/sms-consent" element={<SmsConsent />} />
        <Route path="/" element={<LandingPage />} />

          {/* Login - standalone page */}
          <Route path="/login" element={<Login />} />
          
          {/* Main app with layout - PROTECTED */}
          <Route path="/app" element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }>
            <Route index element={<Navigate to="/app/broker" replace />} />
            
            {/* Broker */}
            <Route path="broker">
              <Route index element={<BrokerDashboard />} />
              <Route path="loads" element={<BrokerLoads />} />
              <Route path="loads/:id" element={<BrokerLoadDetail />} />
              <Route path="create" element={<BrokerCreateLoad />} />
              <Route path="customers" element={<BrokerCustomers />} />
              <Route path="documents/pending" element={<BrokerPendingDocuments />} />
              <Route path="carriers/create" element={<BrokerCreateCarrier />} />
              <Route path="factoring" element={<BrokerFactoringDashboard />} />
              <Route path="rate-insights" element={<RateInsights />} />
              <Route path="tracking" element={<BrokerTracking />} />
              <Route path="wellsites" element={<WellsiteManager />} />
              <Route path="crm" element={<CRMDashboard />} />
              <Route path="crm/contacts" element={<CRMContacts />} />
              <Route path="tariffs" element={<TariffCalculator />} />
              <Route path="sap-settings" element={<SAPSettings />} />
              <Route path="sustainability" element={<SustainabilityDashboard />} />
              <Route path="admin/roles" element={<AdminRoles />} />
              <Route path="admin/users" element={<AdminUsers />} />
              <Route path="admin/organization" element={<AdminOrganization />} />
              <Route path="admin/organizations" element={<SuperAdminOrganizations />} />
              <Route path="admin/audit" element={<AuditLog />} />
              <Route path="admin/security" element={<SecuritySettings />} />
              <Route path="copilot" element={<AICopilot />} />
              <Route path="alerts" element={<AlertsDashboard />} />
              <Route path="freight-agent" element={<FreightAgent />} />
              <Route path="blockchain" element={<BlockchainLedger />} />
              <Route path="analytics" element={<AnalyticsDashboard />} />
              <Route path="crm/chats" element={<CRMChatSessions />} />
              <Route path="carriers" element={<BrokerCarrierManagement />} />
              <Route path="/app/broker/qr-handoffs" element={<ProtectedRoute><QRHandoffs /></ProtectedRoute>} />
              <Route path="/app/broker/document-signing" element={<ProtectedRoute><DocumentSigning /></ProtectedRoute>} />
              <Route path="/app/broker/load-workflow" element={<ProtectedRoute><LoadWorkflow /></ProtectedRoute>} />
            </Route>
            
            {/* Carrier */}
            <Route path="carrier" element={<CarrierLayout />}>
              <Route path="assigned" element={<CarrierAssignedLoads />} />
              <Route path="board" element={<CarrierLoadBoard />} />
              <Route path="loads/:id" element={<CarrierLoadDetail />} />
              <Route path="factoring" element={<CarrierFactoringHistory />} />
              <Route path="profile" element={<CarrierProfile />} />
              <Route path="carrier-alerts" element={<CarrierLoadAlerts />} />
              <Route path="tracking" element={<CarrierTracking />} />
              <Route path="credit-check" element={<BrokerCreditCheck />} />
              <Route path="oilfield" element={<OilfieldLoadBoard />} />
              <Route path="certifications" element={<CarrierCertifications />} />
              <Route path="loads/:id/pod" element={<CarrierPODCapture />} />
              <Route path="backhaul" element={<CarrierBackhaulAlerts />} />
            </Route>
            
            {/* Shipper */}
            <Route path="shipper">
              <Route index element={<ShipperDashboard />} />
              <Route path="loads" element={<ShipperLoads />} />
              <Route path="loads/:id" element={<ShipperLoadDetail />} />
              <Route path="tracking" element={<ShipperTracking />} />
              <Route path="create" element={<ShipperRequestShipment />} />
              <Route path="profile" element={<ShipperProfile />} />
            </Route>
            
            {/* Accounting */}
            <Route path="accounting">
              <Route index element={<AccountingDashboard />} />
              <Route path="invoicing" element={<AccountingInvoicing />} />
              <Route path="settlements" element={<AccountingSettlements />} />
              <Route path="exports" element={<AccountingExports />} />
              <Route path="integrations" element={<AccountingIntegrations />} />
            </Route>
            
            {/* Fallback */}
            <Route path="*" element={<div style={{ padding: 24 }}>Not found</div>} />
          </Route>
        </Routes>
      <ChatWidget />
      </BrowserRouter>
    </AuthProvider>
  );
}
